// @unmute/dictation — press start, speak, press stop, get text.
//
//   const dictation = new Dictation({ token: () => fetch('/unmute-token').then(r => r.json()).then(j => j.token) })
//   await dictation.start()
//   const { text } = await dictation.stop()
//
// Works in every modern browser and in Electron renderers. Everything between
// the microphone and the finished text happens on the Unmute servers.

import { MicCapture, prewarmAudio, type CaptureHandlers } from './capture'

export const DEFAULT_API_URL = 'wss://api.justunmute.me'
const READY_TIMEOUT_MS = 10_000
const FINAL_TIMEOUT_MS = 60_000
const DEFAULT_HINT_COOLDOWN_MS = 10 * 60_000

export type TokenSource = string | (() => string | Promise<string>)

export interface DictationOptions {
  /** A client token (ut_…) or a function that fetches a fresh one from YOUR
   *  backend. Called once per dictation. Never pass your secret key here. */
  token: TokenSource
  /** Default wss://api.justunmute.me */
  apiUrl?: string
  /** ISO-639-1 language code ('en' default) or 'auto'. */
  language?: string
  /** 'raw' (default): the words as spoken, fixed only in noisy rooms.
   *  'clean': always formatted — punctuation, capitalization, quotation marks,
   *  paragraphs and lists — with only "um"/"uh"-type sounds removed. The
   *  speaker's words are never added to, dropped, replaced or reordered. */
  mode?: 'raw' | 'clean'
  /** Names, product terms and spellings the speaker is likely to use. */
  prompt?: string
  /** Fix misheard words when the room is noisy (default true). */
  correction?: boolean
  /** Remove "um", "uh", stutters and false starts (default false). */
  removeFillers?: boolean
  /** Lowercase the output (default false). */
  lowercase?: boolean
  /** Hard limit for one dictation, in seconds (max and default 600). */
  maxDurationSeconds?: number
  /** Microphone deviceId from navigator.mediaDevices.enumerateDevices(). */
  deviceId?: string
  /** A hint the user has already been shown is not repeated for this long
   *  (across dictations). Default 10 minutes; 0 shows every hint. */
  hintCooldownMs?: number
  /** @internal Test seam: replaces the microphone. */
  _capture?: (handlers: CaptureHandlers) => Pick<MicCapture, 'start' | 'stop'>
}

export type DictationState = 'idle' | 'connecting' | 'recording' | 'processing'

export interface DictationResult {
  text: string
  sessionId: string
  audioSeconds: number
  speechDetected: boolean
  /** Set when no text was returned on purpose: nothing was said, the
   *  recording was too short, or it was too quiet to trust. */
  dropped: { reason: 'too_short' | 'no_speech' | 'quiet_capture'; text?: string } | null
  noisyEnvironment: boolean
  corrected: boolean
  fillersRemoved: boolean
  mode: 'raw' | 'clean'
  /** Clean mode changed the formatting of the text. */
  formatted: boolean
  processingMs: number
  /** Prepaid seconds left on the account after this dictation. */
  balanceSeconds?: number
}

export interface Hint {
  code: 'noisy_environment' | 'too_quiet'
  active: boolean
  /** Ready-to-show copy when active; null when the condition cleared. */
  message: string | null
}

export type DictationErrorCode =
  | 'mic_permission_denied' | 'no_microphone' | 'mic_unavailable'
  | 'token_error' | 'connection_failed' | 'timeout' | 'busy' | 'not_recording' | 'cancelled'
  | 'unauthorized' | 'forbidden' | 'invalid_request' | 'rate_limited' | 'transcription_failed'
  | 'session_timeout' | 'insufficient_balance' | 'internal_error'

export class DictationError extends Error {
  constructor(readonly code: DictationErrorCode, message: string) {
    super(message)
    this.name = 'DictationError'
  }
}

interface Events {
  state: DictationState
  hint: Hint
  level: number
  speech: { atSeconds: number }
  result: DictationResult
  error: DictationError
}

const lastHintShownAt: Record<string, number> = {}

export class Dictation {
  private readonly opts: DictationOptions
  private listeners = new Map<keyof Events, Set<(v: any) => void>>()
  private stateValue: DictationState = 'idle'
  private mic: Pick<MicCapture, 'start' | 'stop'> | null = null
  private ws: WebSocket | null = null
  private ready: Promise<void> | null = null
  private queue: ArrayBuffer[] = []
  private isReady = false
  private failure: DictationError | null = null
  private finalWaiter: { resolve(r: DictationResult): void; reject(e: DictationError): void } | null = null
  private earlyResult: DictationResult | null = null
  private hintsShown = new Set<string>()
  private generation = 0

  constructor(options: DictationOptions) {
    if (!options || !options.token) throw new DictationError('token_error', 'Dictation needs a `token` (a client token or a function returning one)')
    if (typeof options.token === 'string' && options.token.startsWith('sk_')) {
      throw new DictationError('forbidden', 'Never put your secret key in client code. Mint a client token on your server (POST /v1/tokens).')
    }
    this.opts = options
    // Load the audio pipeline now so the first word of the first dictation
    // isn't lost while it spins up. Skipped when a test replaces the mic.
    if (!options._capture) { try { prewarmAudio() } catch { /* non-browser */ } }
  }

  get state(): DictationState { return this.stateValue }

  on<K extends keyof Events>(event: K, fn: (v: Events[K]) => void): () => void {
    let set = this.listeners.get(event)
    if (!set) this.listeners.set(event, (set = new Set()))
    set.add(fn)
    return () => { set.delete(fn) }
  }

  private emit<K extends keyof Events>(event: K, value: Events[K]): void {
    for (const fn of this.listeners.get(event) ?? []) {
      try { fn(value) } catch (e) { setTimeout(() => { throw e }) }
    }
  }

  private setState(s: DictationState): void {
    if (this.stateValue === s) return
    this.stateValue = s
    this.emit('state', s)
  }

  /** Open the microphone and start streaming. Resolves once the mic is live;
   *  the connection finishes in the background and nothing spoken meanwhile
   *  is lost. */
  async start(): Promise<void> {
    if (this.stateValue !== 'idle') throw new DictationError('busy', 'A dictation is already in progress')
    const gen = ++this.generation
    this.reset()
    this.setState('connecting')
    this.ready = this.connect(gen)
    this.ready.catch((e) => this.onFatal(gen, toDictationError(e)))
    const handlers: CaptureHandlers = {
      onFrame: (pcm) => this.sendAudio(pcm),
      onLevel: (rms) => this.emit('level', rms),
    }
    const mic = this.opts._capture ? this.opts._capture(handlers) : new MicCapture(handlers)
    this.mic = mic
    try {
      await mic.start(this.opts.deviceId)
    } catch (e) {
      this.teardown()
      this.setState('idle')
      throw micError(e)
    }
    if (gen !== this.generation) { await mic.stop(); throw new DictationError('cancelled', 'Dictation was cancelled') }
    if (this.failure) { const f = this.failure; await mic.stop(); this.teardown(); this.setState('idle'); throw f }
    this.setState('recording')
  }

  /** Stop recording and wait for the finished text. */
  async stop(): Promise<DictationResult> {
    if (this.earlyResult) { const r = this.earlyResult; this.earlyResult = null; return r }
    if (this.stateValue === 'idle') {
      if (this.failure) { const f = this.failure; this.failure = null; throw f }
      throw new DictationError('not_recording', 'stop() called with no dictation in progress')
    }
    if (this.stateValue === 'processing') throw new DictationError('busy', 'Already stopping')
    const gen = this.generation
    this.setState('processing')
    const final = new Promise<DictationResult>((resolve, reject) => { this.finalWaiter = { resolve, reject } })
    try {
      await this.mic?.stop()
      this.mic = null
      await withTimeout(this.ready!, READY_TIMEOUT_MS, 'Could not reach the dictation service')
      if (this.failure) throw this.failure
      this.flushQueue()
      this.ws!.send(JSON.stringify({ type: 'stop' }))
      const result = await withTimeout(final, FINAL_TIMEOUT_MS, 'The dictation service did not answer in time')
      return result
    } catch (e) {
      const err = toDictationError(e)
      if (gen === this.generation) { this.teardown(); this.setState('idle') }
      throw err
    } finally {
      this.finalWaiter = null
    }
  }

  /** Abandon the dictation. Nothing is returned; a pending stop() rejects. */
  cancel(): void {
    if (this.stateValue === 'idle') return
    this.generation++
    try { if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify({ type: 'cancel' })) } catch { /* closing anyway */ }
    this.finalWaiter?.reject(new DictationError('cancelled', 'Dictation was cancelled'))
    this.teardown()
    this.setState('idle')
  }

  // ─── internals ───

  private reset(): void {
    this.queue = []
    this.isReady = false
    this.failure = null
    this.earlyResult = null
    this.hintsShown.clear()
  }

  private async connect(gen: number): Promise<void> {
    let token: string
    try {
      token = typeof this.opts.token === 'function' ? await this.opts.token() : this.opts.token
    } catch (e) {
      throw new DictationError('token_error', `Fetching a client token failed: ${e instanceof Error ? e.message : e}`)
    }
    if (typeof token !== 'string' || !token) throw new DictationError('token_error', 'The token function returned no token')
    if (token.startsWith('sk_')) throw new DictationError('forbidden', 'Never put your secret key in client code. Mint a client token on your server.')
    if (gen !== this.generation) throw new DictationError('cancelled', 'Dictation was cancelled')

    const base = (this.opts.apiUrl ?? DEFAULT_API_URL).replace(/^http/, 'ws').replace(/\/+$/, '')
    const ws = new WebSocket(`${base}/v1/listen?token=${encodeURIComponent(token)}`)
    ws.binaryType = 'arraybuffer'
    this.ws = ws
    await new Promise<void>((resolve, reject) => {
      let settled = false
      const done = (err?: DictationError) => {
        if (settled) return
        settled = true
        err ? reject(err) : resolve()
      }
      ws.onopen = () => {
        const o = this.opts
        ws.send(JSON.stringify({
          type: 'start',
          ...(o.mode !== undefined ? { mode: o.mode } : {}),
          ...(o.language !== undefined ? { language: o.language } : {}),
          ...(o.prompt !== undefined ? { prompt: o.prompt } : {}),
          ...(o.correction !== undefined ? { correction: o.correction } : {}),
          ...(o.removeFillers !== undefined ? { remove_fillers: o.removeFillers } : {}),
          ...(o.lowercase !== undefined ? { lowercase: o.lowercase } : {}),
          ...(o.maxDurationSeconds !== undefined ? { max_duration_seconds: o.maxDurationSeconds } : {}),
          encoding: 'pcm_s16le',
          sample_rate: 16000,
        }))
      }
      ws.onmessage = (ev) => {
        if (typeof ev.data !== 'string') return
        let msg: any
        try { msg = JSON.parse(ev.data) } catch { return }
        if (msg.type === 'ready') {
          this.isReady = true
          this.flushQueue()
          done()
          return
        }
        if (msg.type === 'error') {
          const err = new DictationError(msg.code ?? 'internal_error', msg.message ?? 'Dictation failed')
          done(err)
          this.onFatal(gen, err)
          return
        }
        this.onServerMessage(gen, msg)
      }
      ws.onerror = () => done(new DictationError('connection_failed', 'Could not connect to the dictation service'))
      ws.onclose = (ev) => {
        done(new DictationError('connection_failed', `Connection closed before it was ready (${ev.code})`))
        if (gen === this.generation && this.stateValue !== 'idle' && !this.earlyResult) {
          this.onFatal(gen, new DictationError('connection_failed', 'The connection to the dictation service was lost'))
        }
      }
    })
  }

  private onServerMessage(gen: number, msg: any): void {
    if (gen !== this.generation) return
    switch (msg.type) {
      case 'speech_started':
        this.emit('speech', { atSeconds: msg.at_seconds })
        return
      case 'hint':
        return this.onHint(msg)
      case 'final': {
        const r = toResult(msg)
        this.teardown(false)
        if (this.finalWaiter) this.finalWaiter.resolve(r)
        else {
          // The server finished on its own (duration limit): keep the result
          // for the next stop() and announce it.
          this.earlyResult = r
          void this.mic?.stop()
          this.mic = null
        }
        this.setState('idle')
        this.emit('result', r)
        return
      }
    }
  }

  private onHint(msg: { code: Hint['code']; active: boolean; message: string | null }): void {
    const cooldown = this.opts.hintCooldownMs ?? DEFAULT_HINT_COOLDOWN_MS
    if (msg.active) {
      // Same room, three dictations ≠ three nags: the first raise of a
      // dictation respects the cooldown; a re-raise within it is fresh signal.
      const recent = Date.now() - (lastHintShownAt[msg.code] ?? 0) < cooldown
      if (!this.hintsShown.has(msg.code) && recent) return
      this.hintsShown.add(msg.code)
      lastHintShownAt[msg.code] = Date.now()
    } else if (!this.hintsShown.has(msg.code)) {
      return
    }
    this.emit('hint', { code: msg.code, active: msg.active, message: msg.message })
  }

  private onFatal(gen: number, err: DictationError): void {
    if (gen !== this.generation || this.failure) return
    this.failure = err
    if (this.finalWaiter) { this.finalWaiter.reject(err); return }
    if (this.stateValue === 'recording' || this.stateValue === 'connecting') {
      void this.mic?.stop()
      this.mic = null
      this.teardown(false)
      this.setState('idle')
      this.emit('error', err)
    }
  }

  private sendAudio(pcm: Int16Array): void {
    const buf = pcm.buffer.slice(pcm.byteOffset, pcm.byteOffset + pcm.byteLength) as ArrayBuffer
    if (this.isReady && this.ws?.readyState === WebSocket.OPEN) this.ws.send(buf)
    else this.queue.push(buf)
  }

  private flushQueue(): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return
    for (const b of this.queue) this.ws.send(b)
    this.queue = []
  }

  private teardown(stopMic = true): void {
    if (stopMic) { void this.mic?.stop(); this.mic = null }
    const ws = this.ws
    this.ws = null
    this.isReady = false
    this.queue = []
    if (ws) {
      ws.onopen = ws.onmessage = ws.onerror = ws.onclose = null
      try { if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) ws.close(1000) } catch { /* gone */ }
    }
  }
}

function toResult(m: any): DictationResult {
  return {
    text: m.text ?? '',
    sessionId: m.session_id,
    audioSeconds: m.audio_seconds,
    speechDetected: !!m.speech_detected,
    dropped: m.dropped ?? null,
    noisyEnvironment: !!m.noisy_environment,
    corrected: !!m.corrected,
    fillersRemoved: !!m.fillers_removed,
    mode: m.mode === 'clean' ? 'clean' : 'raw',
    formatted: !!m.formatted,
    processingMs: m.processing_ms,
    ...(typeof m.balance_seconds === 'number' ? { balanceSeconds: m.balance_seconds } : {}),
  }
}

function micError(e: unknown): DictationError {
  const name = (e as { name?: string })?.name
  if (name === 'NotAllowedError' || name === 'SecurityError') return new DictationError('mic_permission_denied', 'Microphone access was denied')
  if (name === 'NotFoundError' || name === 'OverconstrainedError') return new DictationError('no_microphone', 'No microphone was found')
  if (typeof navigator === 'undefined' || !navigator.mediaDevices) return new DictationError('mic_unavailable', 'Microphone capture needs a secure context (https or localhost)')
  return new DictationError('mic_unavailable', `Could not open the microphone: ${e instanceof Error ? e.message : e}`)
}

function toDictationError(e: unknown): DictationError {
  return e instanceof DictationError ? e : new DictationError('internal_error', e instanceof Error ? e.message : String(e))
}

function withTimeout<T>(p: Promise<T>, ms: number, message: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const t = setTimeout(() => reject(new DictationError('timeout', message)), ms)
    p.then((v) => { clearTimeout(t); resolve(v) }, (e) => { clearTimeout(t); reject(e) })
  })
}
